'use client';

import React from 'react';

import { Box, type BoxProps } from './box';
import { VStack, HStack } from './stack';
import { Skeleton, SkeletonText } from './skeleton';

export interface DataTableSkeletonProps {
  /**
   * Number of rows to show
   */
  rows?: number;
  /**
   * Number of columns to show
   */
  columns?: number;
  /**
   * Whether to show checkbox column
   */
  showCheckbox?: boolean;
  /**
   * Whether to show actions column
   */
  showActions?: boolean;
  /**
   * Height of each row
   */
  rowHeight?: number;
  /**
   * Whether to show header
   */
  showHeader?: boolean;
  /**
   * Whether to show pagination
   */
  showPagination?: boolean;
  /**
   * Whether to show toolbar
   */
  showToolbar?: boolean;
}

export function DataTableSkeleton({
  rows = 5,
  columns = 4,
  showCheckbox = false,
  showActions = false,
  rowHeight = 52,
  showHeader = true,
  showPagination = true,
  showToolbar = true,
}: DataTableSkeletonProps) {
  // const _totalColumns = columns + (showCheckbox ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <VStack gap={4} width="100%">
      {/* Toolbar Skeleton */}
      {showToolbar && (
        <HStack justifyContent="space-between" width="100%" padding={4}>
          <HStack gap={3}>
            {/* Search Input */}
            <Skeleton height="40px" width="300px" />
            {/* Filter Buttons */}
            <Skeleton height="40px" width="100px" />
            <Skeleton height="40px" width="100px" />
          </HStack>
          <HStack gap={3}>
            {/* Action Buttons */}
            <Skeleton height="40px" width="120px" />
            <Skeleton height="40px" width="40px" />
          </HStack>
        </HStack>
      )}

      {/* Table Skeleton */}
      <Box
        width="100%"
        border="1px solid"
        borderColor="#E5E7EB"
        borderRadius="lg"
        overflow="hidden"
      >
        {/* Header Row */}
        {showHeader && (
          <Box backgroundColor="#F9FAFB" borderBottomWidth="1px" borderColor="#E5E7EB">
            <HStack gap={4} padding={4} height={`${rowHeight}px`} alignItems="center">
              {showCheckbox && <Skeleton variant="rectangular" width="20px" height="20px" />}
              {Array.from({ length: columns }).map((_, index) => (
                <Box key={`header-${index}`} flex="1">
                  <Skeleton height="16px" width={`${80 + Math.random() * 40}%`} />
                </Box>
              ))}
              {showActions && (
                <Box width="100px">
                  <Skeleton height="16px" width="60px" />
                </Box>
              )}
            </HStack>
          </Box>
        )}

        {/* Body Rows */}
        <VStack gap={0} align="stretch">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <Box
              key={`row-${rowIndex}`}
              borderBottomWidth={rowIndex < rows - 1 ? '1px' : '0'}
              borderColor="#E5E7EB"
              _hover={{ backgroundColor: '#F9FAFB' }}
            >
              <HStack gap={4} padding={4} height={`${rowHeight}px`} alignItems="center">
                {showCheckbox && <Skeleton variant="rectangular" width="20px" height="20px" />}
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <Box key={`cell-${rowIndex}-${colIndex}`} flex="1">
                    {colIndex === 0 ? (
                      // First column might have more content
                      <VStack gap={1} align="flex-start">
                        <Skeleton height="14px" width={`${60 + Math.random() * 30}%`} />
                        <Skeleton
                          height="12px"
                          width={`${40 + Math.random() * 20}%`}
                          opacity={0.6}
                        />
                      </VStack>
                    ) : (
                      // Other columns
                      <Skeleton height="14px" width={`${50 + Math.random() * 40}%`} />
                    )}
                  </Box>
                ))}
                {showActions && (
                  <HStack gap={2} width="100px" justifyContent="flex-end">
                    <Skeleton variant="circular" width="32px" height="32px" />
                    <Skeleton variant="circular" width="32px" height="32px" />
                  </HStack>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Pagination Skeleton */}
      {showPagination && (
        <HStack justifyContent="space-between" width="100%" padding={4}>
          <Skeleton height="14px" width="200px" />
          <HStack gap={2}>
            <Skeleton height="32px" width="32px" />
            <Skeleton height="32px" width="32px" />
            <Skeleton height="32px" width="32px" />
            <Skeleton height="32px" width="32px" />
            <Skeleton height="32px" width="32px" />
          </HStack>
          <HStack gap={2}>
            <Skeleton height="32px" width="80px" />
            <Skeleton height="32px" width="120px" />
          </HStack>
        </HStack>
      )}
    </VStack>
  );
}

// Form skeleton component
export function FormSkeleton({ fields = 5 }: { fields?: number }) {
  return (
    <VStack gap={6} width="100%">
      {Array.from({ length: fields }).map((_, index) => (
        <VStack key={`field-${index}`} gap={2} align="stretch" width="100%">
          {/* Label */}
          <Skeleton height="14px" width="120px" />
          {/* Input */}
          <Skeleton height="40px" width="100%" />
          {/* Helper text */}
          {Math.random() > 0.5 && <Skeleton height="12px" width="60%" opacity={0.6} />}
        </VStack>
      ))}
      {/* Submit button */}
      <HStack gap={3} marginTop={4}>
        <Skeleton height="40px" width="120px" />
        <Skeleton height="40px" width="100px" variant="rectangular" />
      </HStack>
    </VStack>
  );
}

export type CardSkeletonProps = Omit<BoxProps, 'children'>;

/**
 * Card skeleton component - Supports accessibility props like aria-label
 *
 * @example
 * ```tsx
 * <CardSkeleton aria-label="Loading card" />
 * ```
 */
export function CardSkeleton(props: CardSkeletonProps) {
  return (
    <Box
      border="1px solid"
      borderColor="#E5E7EB"
      borderRadius="lg"
      padding={6}
      width="100%"
      {...props}
    >
      <VStack gap={4} align="stretch">
        {/* Header */}
        <HStack justifyContent="space-between">
          <VStack gap={2} align="flex-start" flex="1">
            <Skeleton height="20px" width="60%" />
            <Skeleton height="14px" width="40%" opacity={0.6} />
          </VStack>
          <Skeleton variant="circular" width="40px" height="40px" />
        </HStack>

        {/* Content */}
        <VStack gap={3} align="stretch">
          <SkeletonText lineClamp={3} />
        </VStack>

        {/* Footer */}
        <HStack gap={2} marginTop={2}>
          <Skeleton height="24px" width="80px" />
          <Skeleton height="24px" width="80px" />
          <Skeleton height="24px" width="80px" />
        </HStack>
      </VStack>
    </Box>
  );
}

// List item skeleton
export function ListItemSkeleton() {
  return (
    <HStack gap={4} padding={4} borderBottomWidth="1px" borderColor="#E5E7EB" width="100%">
      <Skeleton variant="circular" width="48px" height="48px" />
      <VStack gap={2} align="flex-start" flex="1">
        <Skeleton height="16px" width="70%" />
        <Skeleton height="14px" width="50%" opacity={0.6} />
      </VStack>
      <VStack gap={1} align="flex-end">
        <Skeleton height="14px" width="60px" />
        <Skeleton height="20px" width="80px" />
      </VStack>
    </HStack>
  );
}

export interface WidgetSkeletonProps extends Omit<BoxProps, 'children'> {
  /**
   * Variant of the widget skeleton
   * @default 'stat'
   */
  variant?: 'stat' | 'chart' | 'list';
}

/**
 * Dashboard widget skeleton - Supports accessibility props like aria-label
 *
 * @example
 * ```tsx
 * <WidgetSkeleton variant="stat" aria-label="Loading widget" />
 * ```
 */
export function WidgetSkeleton({ variant = 'stat', ...props }: WidgetSkeletonProps) {
  if (variant === 'stat') {
    return (
      <Box
        border="1px solid"
        borderColor="#E5E7EB"
        borderRadius="lg"
        padding={6}
        width="100%"
        {...props}
      >
        <VStack gap={3} align="flex-start">
          <HStack justifyContent="space-between" width="100%">
            <Skeleton height="14px" width="100px" />
            <Skeleton variant="circular" width="32px" height="32px" />
          </HStack>
          <Skeleton height="32px" width="120px" />
          <HStack gap={2}>
            <Skeleton height="12px" width="60px" />
            <Skeleton height="12px" width="40px" />
          </HStack>
        </VStack>
      </Box>
    );
  }

  if (variant === 'chart') {
    return (
      <Box
        border="1px solid"
        borderColor="#E5E7EB"
        borderRadius="lg"
        padding={6}
        width="100%"
        {...props}
      >
        <VStack gap={4} align="stretch">
          <Skeleton height="20px" width="150px" />
          <Skeleton height="200px" width="100%" />
          <HStack gap={4} justifyContent="center">
            <HStack gap={2}>
              <Skeleton variant="circular" width="12px" height="12px" />
              <Skeleton height="12px" width="60px" />
            </HStack>
            <HStack gap={2}>
              <Skeleton variant="circular" width="12px" height="12px" />
              <Skeleton height="12px" width="60px" />
            </HStack>
          </HStack>
        </VStack>
      </Box>
    );
  }

  // List variant
  return (
    <Box
      border="1px solid"
      borderColor="#E5E7EB"
      borderRadius="lg"
      padding={6}
      width="100%"
      {...props}
    >
      <VStack gap={4} align="stretch">
        <Skeleton height="20px" width="150px" />
        <VStack gap={0} align="stretch">
          {Array.from({ length: 5 }).map((_, index) => (
            <ListItemSkeleton key={index} />
          ))}
        </VStack>
      </VStack>
    </Box>
  );
}

// Export with display names for debugging
DataTableSkeleton.displayName = 'DataTableSkeleton';
FormSkeleton.displayName = 'FormSkeleton';
CardSkeleton.displayName = 'CardSkeleton';
ListItemSkeleton.displayName = 'ListItemSkeleton';
WidgetSkeleton.displayName = 'WidgetSkeleton';
